import type {
  APIChannel,
  APIGatewayGuild,
  APIGuildMember,
  APIGuildScheduledEvent,
  APIMessage,
  APIMessageReference,
  APIPresence,
  APIReadState,
  APIRole,
  APIThreadMember,
  APIUser,
  APIUserGuildSettings,
  APIUserSettings,
} from '../contracts/chat-types.js';
import type { GatewayStatus } from '../gateway/gateway-client.js';

import type { Snowflake } from '../contracts/snowflake.js';

export type { Snowflake };

export type ConnectionState = {
  status: GatewayStatus;
  /** Próxima tentativa (ms), quando `reconnecting`. */
  retryInMs: number | null;
  /** Fechou de vez: 4004 (sessão de login inválida) ou 4012 (versão). */
  fatalCode: number | null;
};

/** Servidor sem as listas (canais, threads e membros ficam nas suas tabelas). */
export type GuildRecord = Omit<
  APIGatewayGuild,
  'channels' | 'threads' | 'members' | 'roles'
> & { roles: Record<Snowflake, APIRole> };

/** Membro sem o `user` (as pessoas ficam em `users`). */
export type MemberRecord = Omit<APIGuildMember, 'user'> & {
  user_id: Snowflake;
};

export type PresenceRecord = {
  status: APIPresence['status'];
  client_status: APIPresence['client_status'];
  activities: unknown[];
};

/** Mensagens carregadas de um canal: um trecho contíguo, em ordem crescente. */
export type MessageList = {
  ids: Snowflake[];
  byId: Record<Snowflake, APIMessage>;
  /** Há mensagens antes da primeira carregada. */
  hasMoreBefore: boolean;
  /** Há mensagens depois da última (o trecho não chega ao fim do canal). */
  hasMoreAfter: boolean;
  /** Já carregou alguma vez (a tela mostra esqueleto até lá). */
  loaded: boolean;
};

/** Mensagem que a pessoa mandou e ainda não voltou da API. */
export type PendingMessage = {
  nonce: string;
  channelId: Snowflake;
  content: string;
  createdAt: number;
  /** `queued`: sem conexão, sai quando o gateway voltar; `failed`: recusada. */
  status: 'sending' | 'queued' | 'failed';
  error: string | null;
  messageReference?: APIMessageReference | null;
  attachments?: Array<{ name: string; size?: number }>;
};

export type ChatState = {
  connection: ConnectionState;
  me: APIUser | null;
  users: Record<Snowflake, APIUser>;
  guilds: Record<Snowflake, GuildRecord>;
  /** Ordem dos servidores (a do READY, depois a das preferências). */
  guildIds: Snowflake[];
  channels: Record<Snowflake, APIChannel>;
  /** Canais de cada servidor (sem threads). */
  guildChannelIds: Record<Snowflake, Snowflake[]>;
  /** Threads conhecidas de cada servidor (as ativas e as abertas na sessão). */
  guildThreadIds: Record<Snowflake, Snowflake[]>;
  privateChannelIds: Snowflake[];
  members: Record<Snowflake, Record<Snowflake, MemberRecord>>;
  presences: Record<Snowflake, PresenceRecord>;
  messages: Record<Snowflake, MessageList>;
  pending: Record<Snowflake, PendingMessage[]>;
  readStates: Record<Snowflake, APIReadState>;
  /** Quem está digitando: canal → pessoa → quando expira (ms). */
  typing: Record<Snowflake, Record<Snowflake, number>>;
  /** Threads em que a pessoa entrou. */
  joinedThreads: Record<Snowflake, APIThreadMember>;
  scheduledEvents: Record<Snowflake, Record<Snowflake, APIGuildScheduledEvent>>;
  userSettings: APIUserSettings | null;
  /** Por servidor; as DMs ficam em `@me`. */
  guildSettings: Record<string, APIUserGuildSettings>;
  /** Notas nos perfis (pessoa → nota); nulo até carregar (`GET /users/@me/notes`). */
  notes: Record<Snowflake, string> | null;
};

export function initialChatState(): ChatState {
  return {
    connection: { status: 'disconnected', retryInMs: null, fatalCode: null },
    me: null,
    users: {},
    guilds: {},
    guildIds: [],
    channels: {},
    guildChannelIds: {},
    guildThreadIds: {},
    privateChannelIds: [],
    members: {},
    presences: {},
    messages: {},
    pending: {},
    readStates: {},
    typing: {},
    joinedThreads: {},
    scheduledEvents: {},
    userSettings: null,
    guildSettings: {},
    notes: null,
  };
}

export const DMS_SETTINGS_KEY = '@me';

export function emptyMessageList(): MessageList {
  return {
    ids: [],
    byId: {},
    hasMoreBefore: true,
    hasMoreAfter: false,
    loaded: false,
  };
}
